// mattermost-credentials-file.ts — the ONE reader for
// `<workspace>/.mattermost/credentials.json` (what `avatar comm connect`
// writes: chmod 600, gitignored).
//
// Extracted in P19 review follow-up: the factory and the deps-builder each
// had their own copy of this parse-and-guard logic. Two readers of the same
// file drift the moment a field is added to one and not the other — the
// same "both ends individually correct, the JOIN wrong" shape as Bug J
// (actor_id namespaces) and Bug N (baseSha branch-name vs oid). One reader
// makes that class of drift impossible here.
//
// Fail-soft on purpose: ANY problem (missing file, bad JSON, missing token)
// reads as `undefined` so callers degrade to local-only mode rather than
// throwing mid-run. The token is returned for use and is never logged,
// echoed, or placed into any error message this module produces.
import { readFile } from "node:fs/promises";
import { join } from "node:path";

export interface MattermostCredentialsFile {
  mattermostUrl?: string;
  botToken?: string;
  teamId?: string;
  defaultChannelId?: string;
  defaultChannelName?: string;
}

export async function readMattermostCredentials(
  workspaceRoot: string,
): Promise<MattermostCredentialsFile | undefined> {
  try {
    const raw = await readFile(join(workspaceRoot, ".mattermost", "credentials.json"), "utf8");
    const parsed = JSON.parse(raw) as MattermostCredentialsFile;
    if (typeof parsed.botToken !== "string" || parsed.botToken.length === 0) return undefined;
    return parsed;
  } catch {
    return undefined;
  }
}

/**
 * The API origin to talk to: operator config wins over the credentials
 * file, so a self-hosted Mattermost whose API origin differs from what
 * `comm connect` recorded can be stated explicitly (same reason
 * `release.gitlab_base_url` was added in P18).
 */
export function resolveMattermostBaseUrl(
  configuredBaseUrl: string | undefined,
  credentials: MattermostCredentialsFile,
): string | undefined {
  const baseUrl = configuredBaseUrl ?? credentials.mattermostUrl;
  return baseUrl !== undefined && baseUrl.length > 0 ? baseUrl : undefined;
}
